/**
 * Mind-map auto-layout.
 *
 * Connectors already describe the tree — a connector from A to a node-anchored
 * B is read as "A is B's parent" — so laying it out is just: walk that graph
 * from a chosen root, and place each node one gap to the right (or below) of
 * its parent, centred over its own children.
 *
 * This is a simplified Reingold–Tilford layout: each leaf claims a unique slot
 * along the cross axis in traversal order (so sibling subtrees never
 * overlap), and every internal node centres over the span of its children
 * once they are placed. It does not try to minimise total width the way a
 * full tidy-tree layout would — for the size of mind map people actually draw
 * on a whiteboard, simple and correct is worth more than optimal packing.
 */
import type { NodeId, Vec } from './types.js';

export interface MindmapLayoutOptions {
  /** Gap between the far edge of a column and the next, in board units. */
  gapMain?: number;
  /** Gap between sibling nodes along the cross axis. */
  gapCross?: number;
  direction?: 'right' | 'down';
}

export function layoutMindmap(
  boxes: ReadonlyMap<NodeId, { w: number; h: number }>,
  /** parentId -> ordered child ids, built from connectors. */
  edges: ReadonlyMap<NodeId, NodeId[]>,
  rootId: NodeId,
  origin: Vec,
  options: MindmapLayoutOptions = {},
): Map<NodeId, Vec> {
  const gapMain = options.gapMain ?? 90;
  const gapCross = options.gapCross ?? 32;
  const horizontal = (options.direction ?? 'right') === 'right';

  const positions = new Map<NodeId, Vec>();
  if (!boxes.has(rootId)) return positions;

  // ---- pass 1: depth of every reachable node, and the widest box per depth,
  // so every column lines up on its own axis rather than each node's own size.
  const depthOf = new Map<NodeId, number>();
  const columnExtent = new Map<number, number>();

  (function walk(id: NodeId, depth: number, visited: Set<NodeId>): void {
    if (visited.has(id) || !boxes.has(id)) return;
    visited.add(id);
    depthOf.set(id, depth);
    const box = boxes.get(id)!;
    const extent = horizontal ? box.w : box.h;
    columnExtent.set(depth, Math.max(columnExtent.get(depth) ?? 0, extent));
    for (const child of edges.get(id) ?? []) walk(child, depth + 1, visited);
  })(rootId, 0, new Set());

  // ---- column main-axis offsets, cumulative down the tree.
  const columnMain = new Map<number, number>();
  const maxDepth = Math.max(...depthOf.values());
  let cursor = horizontal ? origin.x : origin.y;
  for (let d = 0; d <= maxDepth; d++) {
    columnMain.set(d, cursor);
    cursor += (columnExtent.get(d) ?? 0) + gapMain;
  }

  // ---- pass 2: cross-axis position, post-order so parents can centre over
  // children that are already placed. Leaves simply claim the next free slot.
  let crossCursor = horizontal ? origin.y : origin.x;
  const placed = new Set<NodeId>();

  function assignCross(id: NodeId): number {
    if (placed.has(id)) {
      const p = positions.get(id)!;
      return horizontal ? p.y : p.x;
    }
    placed.add(id);
    const box = boxes.get(id)!;
    const size = horizontal ? box.h : box.w;
    const children = (edges.get(id) ?? []).filter((c) => depthOf.has(c) && !placed.has(c));

    let cross: number;
    if (children.length === 0) {
      cross = crossCursor;
      crossCursor += size + gapCross;
    } else {
      const childCentres = children.map((c) => {
        const childCross = assignCross(c);
        const childBox = boxes.get(c)!;
        const childSize = horizontal ? childBox.h : childBox.w;
        return childCross + childSize / 2;
      });
      const mid = (childCentres[0]! + childCentres[childCentres.length - 1]!) / 2;
      cross = mid - size / 2;
    }

    const main = columnMain.get(depthOf.get(id)!)!;
    positions.set(id, horizontal ? { x: main, y: cross } : { x: cross, y: main });
    return cross;
  }

  assignCross(rootId);
  return positions;
}
